/**
 * NLQ Redux Slice — state management for Natural Language Query.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nlqService, { NLQQueryRequest, NLQQueryResponse } from '../services/nlqService';

interface NLQState {
  loading: boolean;
  error: string | null;
  result: NLQQueryResponse | null;
  queryHistory: Array<{ query: string; response: NLQQueryResponse }>;
}

const initialState: NLQState = {
  loading: false,
  error: null,
  result: null,
  queryHistory: [],
};

export const submitNLQQuery = createAsyncThunk(
  'nlq/submitQuery',
  async (request: NLQQueryRequest, { rejectWithValue }) => {
    try {
      const response = await nlqService.submitQuery(request);
      return { request, response };
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to execute NLQ query';
      return rejectWithValue(message);
    }
  },
);

const nlqSlice = createSlice({
  name: 'nlq',
  initialState,
  reducers: {
    clearNLQResult: (state) => {
      state.result = null;
      state.error = null;
    },
    clearNLQError: (state) => {
      state.error = null;
    },
    clearNLQHistory: (state) => {
      state.queryHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitNLQQuery.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.result = null;
      })
      .addCase(submitNLQQuery.fulfilled, (state, action) => {
        state.loading = false;
        state.result = action.payload.response;
        state.queryHistory.push({
          query: action.payload.request.query,
          response: action.payload.response,
        });
        // Cap history to last 50 entries to prevent unbounded memory growth
        if (state.queryHistory.length > 50) {
          state.queryHistory = state.queryHistory.slice(-50);
        }
      })
      .addCase(submitNLQQuery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearNLQResult, clearNLQError, clearNLQHistory } = nlqSlice.actions;
export default nlqSlice.reducer;
